import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Field } from "./FormField";
import { fullUrl } from "./ImageUpload";
import { eur } from "../helpers";
import { Building2, FileText, BarChart3, ScrollText, MessageCircle, Mail, Plus, Trash2, Banknote, Upload, FolderOpen } from "lucide-react";

const DOC_CATEGORIES = ["Contratto di gestione", "Contratto di locazione", "APE", "Planimetria", "Visura catastale", "SCIA/SUAP", "CIR/CIN", "Polizza assicurativa", "Certificazioni impianti", "Libretto caldaia", "Inventario iniziale", "Verbali consegna", "Foto appartamento", "Altri documenti"];
const onlyDigits = (s) => (s || "").replace(/[^\d]/g, "");

export default function OwnerDetailDialog({ open, onOpenChange, owner, apts, onSaved }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState("economics");
  const [newDoc, setNewDoc] = useState({ title: "", category: DOC_CATEGORIES[0], expiry_date: "", notes: "" });
  const [uploading, setUploading] = useState(false);
  const [payout, setPayout] = useState({ amount: 0, date: new Date().toISOString().slice(0, 10), method: "Bonifico", reference: "", notes: "" });

  const load = useCallback(() => {
    if (!owner?.id) return;
    api.get(`/owners/${owner.id}/summary`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
    setDocs(owner.documents || []);
  }, [owner]);
  useEffect(() => { if (open) { load(); setTab("economics"); } }, [open, load]);

  if (!open || !owner) return null;
  const ownerApts = apts.filter((a) => a.owner_id === owner.id);
  const phone = owner.whatsapp || owner.phone || "";

  const uploadDoc = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!newDoc.title.trim()) { toast.error("Inserisci un titolo documento"); e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const doc = { id: Date.now().toString(), title: newDoc.title, category: newDoc.category, url: r.data.url, name: f.name, uploaded_at: new Date().toISOString(), expiry_date: newDoc.expiry_date, notes: newDoc.notes };
      const updated = [...docs, doc];
      await api.put(`/owners/${owner.id}`, { ...owner, documents: updated });
      setDocs(updated); setNewDoc({ title: "", category: DOC_CATEGORIES[0], expiry_date: "", notes: "" });
      toast.success("Documento caricato"); onSaved?.();
    } catch { toast.error("Errore upload"); }
    setUploading(false); e.target.value = "";
  };
  const removeDoc = async (id) => {
    const updated = docs.filter((d) => d.id !== id);
    await api.put(`/owners/${owner.id}`, { ...owner, documents: updated });
    setDocs(updated); toast.success("Documento rimosso"); onSaved?.();
  };

  const addPayout = async () => {
    if (!payout.amount) { toast.error("Inserisci l'importo"); return; }
    try { await api.post(`/owners/${owner.id}/payouts`, payout); toast.success("Bonifico registrato"); setPayout({ amount: 0, date: new Date().toISOString().slice(0, 10), method: "Bonifico", reference: "", notes: "" }); load(); }
    catch { toast.error("Errore"); }
  };
  const delPayout = async (pid) => { await api.delete(`/owners/${owner.id}/payouts/${pid}`); load(); };

  const S = ({ label, value, cls = "text-slate-900" }) => (
    <Card className="border-slate-200 p-3"><div className="text-xs text-slate-500">{label}</div><div className={`font-head text-lg font-semibold ${cls}`}>{value}</div></Card>
  );
  const links = [
    { key: "apartments", label: "Appartamenti", icon: Building2, on: () => { onOpenChange(false); navigate("/apartments"); } },
    { key: "reports", label: "Rendiconti mensili", icon: FileText, on: () => { onOpenChange(false); navigate("/accounting"); } },
    { key: "financial", label: "Financial Engine", icon: BarChart3, on: () => { onOpenChange(false); navigate("/financial-engine"); } },
    { key: "contracts", label: "Contratti", icon: ScrollText, on: () => { onOpenChange(false); navigate("/financial-engine"); } },
    { key: "docs", label: "Documenti", icon: FolderOpen, on: () => setTab("documents") },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, on: () => { if (!phone) return toast.error("Numero mancante"); window.open(`https://wa.me/${onlyDigits(phone)}`, "_blank"); } },
    { key: "email", label: "Email", icon: Mail, on: () => { if (!owner.email) return toast.error("Email mancante"); window.open(`mailto:${owner.email}`, "_blank"); } },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="owner-detail-dialog">
        <DialogHeader>
          <DialogTitle>{owner.name}</DialogTitle>
          <DialogDescription>{ownerApts.length} immobili · {owner.commission_pct}% · {{ commissione: "Commissione %", canone_fisso: "Canone fisso", mista: "Gestione mista", revenue_share: "Revenue share" }[owner.management_type] || "—"}</DialogDescription>
        </DialogHeader>

        {/* Quick links */}
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-7">
          {links.map((l) => (
            <button key={l.key} data-testid={`owner-link-${l.key}`} onClick={l.on} className="flex flex-col items-center gap-1 rounded-lg border border-slate-200 py-2.5 text-[11px] text-slate-600 hover:border-primary hover:text-primary">
              <l.icon size={16} /> {l.label}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-2 inline-flex rounded-lg border border-slate-200 bg-white p-1">
          {[["economics", "Storico economico"], ["documents", "Documenti"], ["apartments", "Appartamenti"]].map(([k, lb]) => (
            <button key={k} data-testid={`owner-tab-${k}`} onClick={() => setTab(k)} className={`rounded-md px-3 py-1 text-sm font-medium ${tab === k ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100"}`}>{lb}</button>
          ))}
        </div>

        {tab === "economics" && summary && (
          <div className="space-y-4" data-testid="owner-economics">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <S label="Totale incassato" value={eur(summary.total_collected)} />
              <S label="Commissioni maturate" value={eur(summary.commissions_accrued)} cls="text-primary" />
              <S label="Costi sostenuti" value={eur(summary.costs)} cls="text-amber-700" />
              <S label="Liquidato al proprietario" value={eur(summary.total_paid)} />
              <S label="Dovuto al proprietario" value={eur(summary.owed_to_owner)} />
              <S label="Saldo da liquidare" value={eur(summary.balance_to_pay)} cls={summary.balance_to_pay > 0 ? "text-rose-600" : "text-emerald-700"} />
            </div>
            {/* add payout */}
            <Card className="border-slate-200 p-3">
              <div className="mb-2 text-xs font-semibold uppercase text-slate-400">Registra bonifico</div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                <Field label="Importo (€)"><Input data-testid="payout-amount" type="number" step="0.01" value={payout.amount} onChange={(e) => setPayout((p) => ({ ...p, amount: +e.target.value }))} /></Field>
                <Field label="Data"><Input type="date" value={payout.date} onChange={(e) => setPayout((p) => ({ ...p, date: e.target.value }))} /></Field>
                <Field label="Metodo"><Input value={payout.method} onChange={(e) => setPayout((p) => ({ ...p, method: e.target.value }))} /></Field>
                <Field label="Riferimento"><Input value={payout.reference} onChange={(e) => setPayout((p) => ({ ...p, reference: e.target.value }))} /></Field>
                <div className="flex items-end"><Button data-testid="payout-add-btn" onClick={addPayout} className="w-full gap-1 bg-primary hover:bg-primary/90"><Plus size={14} /> Aggiungi</Button></div>
              </div>
            </Card>
            {/* payout history */}
            <div>
              <div className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase text-slate-400"><Banknote size={12} /> Storico bonifici ({summary.payouts.length})</div>
              <div className="space-y-1.5" data-testid="payout-history">
                {summary.payouts.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm">
                    <span className="text-slate-700">{p.date} · <b>{eur(p.amount)}</b> · {p.method}{p.reference ? ` · ${p.reference}` : ""}</span>
                    <button onClick={() => delPayout(p.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                  </div>
                ))}
                {summary.payouts.length === 0 && <p className="text-xs text-slate-400">Nessun bonifico registrato</p>}
              </div>
            </div>
          </div>
        )}

        {tab === "documents" && (
          <div className="space-y-4" data-testid="owner-documents">
            <Card className="border-slate-200 p-3">
              <div className="mb-2 text-xs font-semibold uppercase text-slate-400">Aggiungi documento</div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Field label="Titolo"><Input data-testid="owner-doc-title" value={newDoc.title} onChange={(e) => setNewDoc((d) => ({ ...d, title: e.target.value }))} /></Field>
                <Field label="Categoria">
                  <Select value={newDoc.category} onValueChange={(v) => setNewDoc((d) => ({ ...d, category: v }))}>
                    <SelectTrigger data-testid="owner-doc-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Scadenza (opz.)"><Input type="date" value={newDoc.expiry_date} onChange={(e) => setNewDoc((d) => ({ ...d, expiry_date: e.target.value }))} /></Field>
                <div className="flex items-end">
                  <label className="flex w-full cursor-pointer items-center justify-center gap-1 rounded-md border border-dashed border-slate-300 py-2 text-xs text-slate-500 hover:border-primary" data-testid="owner-doc-upload">
                    <Upload size={14} /> {uploading ? "…" : "Carica file"}
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={uploadDoc} />
                  </label>
                </div>
              </div>
            </Card>
            <div className="space-y-1.5">
              {docs.map((d) => (
                <div key={d.id} data-testid={`owner-doc-${d.id}`} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm">
                  <span className="flex items-center gap-2"><FileText size={14} className="text-primary" /><a href={fullUrl(d.url)} target="_blank" rel="noreferrer" className="font-medium text-slate-800 hover:underline">{d.title}</a><Badge className="bg-slate-100 text-slate-600">{d.category}</Badge>{d.expiry_date && <span className="text-xs text-amber-600">scad. {d.expiry_date}</span>}</span>
                  <button onClick={() => removeDoc(d.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                </div>
              ))}
              {docs.length === 0 && <p className="text-xs text-slate-400">Nessun documento caricato</p>}
            </div>
          </div>
        )}

        {tab === "apartments" && (
          <div className="space-y-2" data-testid="owner-apartments">
            {ownerApts.map((a) => (
              <button key={a.id} onClick={() => { onOpenChange(false); navigate(`/apartments/${a.id}`); }} className="flex w-full items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm hover:border-primary">
                <span className="flex items-center gap-2 font-medium text-slate-800"><Building2 size={14} /> {a.name}</span>
                <span className="text-xs text-slate-400">{a.code}</span>
              </button>
            ))}
            {ownerApts.length === 0 && <p className="text-xs text-slate-400">Nessun immobile collegato</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
